import React, { useEffect, useRef } from "react"
import { View } from "react-native"

import type { StyleProp, ViewStyle } from "react-native"
import type { Alignment, Fit, RiveRef } from "rive-react-native"
import { Layout, Rive, Alignment as RiveAlignment, Fit as RiveFit } from "@rive-app/canvas"

import { riveFileMap } from "./riveUtils"

export interface AvatarRiveProps {
  resourceName: string
  stateMachineName?: string
  fit?: Fit
  alignment?: Alignment
  style?: StyleProp<ViewStyle>
  onRiveInstanceChange?: (instance: RiveRef | Rive | null) => void
}

/**
 * Web implementation of AvatarRive using @rive-app/canvas.
 * This component renders a Rive animation to an HTML canvas element.
 */
const AvatarRive: React.FC<AvatarRiveProps> = ({
  resourceName,
  fit: _fit = undefined,
  alignment: _alignment = undefined,
  style,
  onRiveInstanceChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const riveInstanceRef = useRef<Rive | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  /**
   * Resize the canvas to match its display size with proper DPI handling
   */
  const resizeCanvas = React.useCallback(() => {
    const canvas = canvasRef.current
    const rive = riveInstanceRef.current

    if (!canvas || !rive) return

    const devicePixelRatio = window.devicePixelRatio || 1
    const { width, height } = canvas.getBoundingClientRect()

    // Use container dimensions, fallback to reasonable defaults
    const resolvedWidth = width || 400
    const resolvedHeight = height || 400

    // Update canvas internal resolution
    if (canvas.width !== resolvedWidth * devicePixelRatio) {
      canvas.width = resolvedWidth * devicePixelRatio
    }
    if (canvas.height !== resolvedHeight * devicePixelRatio) {
      canvas.height = resolvedHeight * devicePixelRatio
    }

    // Notify Rive to resize its drawing surface
    rive.resizeDrawingSurfaceToCanvas()
  }, [])

  /**
   * Create and initialize the Rive instance
   */
  useEffect(() => {
    const canvas = canvasRef.current
    const riveSource = riveFileMap[resourceName]

    if (!canvas || !riveSource || !resourceName) {
      onRiveInstanceChange?.(null)
      return
    }

    // Cleanup any existing instance
    if (riveInstanceRef.current) {
      try {
        riveInstanceRef.current.cleanup()
      } catch (error) {
        console.warn("[AvatarRive.web] cleanup warning", error)
      }
      riveInstanceRef.current = null
    }

    let isMounted = true

    // Set a provisional canvas size so Rive has something to render into while loading.
    // The real size is applied in onLoad — by the time the .riv file has downloaded and
    // parsed, the browser has had ample time to complete layout, so getBoundingClientRect
    // returns the true dimensions there. No RAF is needed (and using one would introduce
    // a race: if the effect re-runs before the RAF fires, the old Rive instance would not
    // be cleaned up and would continue rendering to the canvas alongside the new one).
    const devicePixelRatio = window.devicePixelRatio || 1
    const { width: initialWidth, height: initialHeight } = canvas.getBoundingClientRect()
    canvas.width = (initialWidth || 400) * devicePixelRatio
    canvas.height = (initialHeight || 400) * devicePixelRatio

    // Create Rive instance with proper layout configuration
    // Mimics native Fit.Cover and Alignment.BottomCenter
    const riveInstance = new Rive({
      src: riveSource,
      canvas,
      autoplay: true,
      stateMachines: "State Machine 1",
      layout: new Layout({
        fit: RiveFit.Cover, // Match native Fit.Cover
        alignment: RiveAlignment.BottomCenter, // Match native Alignment.BottomCenter
      }),
      onLoad: () => {
        if (!isMounted) return

        // Re-measure the canvas now that layout has settled (the file download/parse
        // gives the browser plenty of time) and update the Rive drawing surface to
        // match. This corrects the provisional 400×400 size set above when the canvas
        // had not yet been laid out.
        resizeCanvas()
        onRiveInstanceChange?.(riveInstance)

        // Setup resize observer after initial sizing is correct
        if (typeof ResizeObserver !== "undefined" && canvas) {
          resizeObserverRef.current = new ResizeObserver(() => {
            resizeCanvas()
          })
          resizeObserverRef.current.observe(canvas)
        }
      },
      onLoadError: (error) => {
        console.error("[AvatarRive.web] Failed to load Rive asset:", error)
        onRiveInstanceChange?.(null)
      },
    })

    // Assign to ref immediately after creation (not in onLoad) so that the cleanup
    // function can always stop the Rive render loop — even if the effect re-runs
    // before onLoad fires (e.g. resourceName changes while the file is still loading).
    // Without this, cleanup would find riveInstanceRef.current === null, skip cleanup(),
    // and the old Rive would keep rendering to the canvas alongside the new one.
    riveInstanceRef.current = riveInstance

    // Handle window resize
    const handleWindowResize = () => {
      resizeCanvas()
    }
    window.addEventListener("resize", handleWindowResize)

    // Cleanup
    return () => {
      isMounted = false
      window.removeEventListener("resize", handleWindowResize)

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }

      onRiveInstanceChange?.(null)

      if (riveInstanceRef.current) {
        try {
          riveInstanceRef.current.cleanup()
        } catch (error) {
          console.warn("[AvatarRive.web] cleanup warning", error)
        }
        riveInstanceRef.current = null
      }
    }
  }, [resourceName, onRiveInstanceChange, resizeCanvas])

  return (
    <View style={style}>
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </View>
  )
}

export default AvatarRive
